import { motion } from "motion/react";
import { BlankSlate } from "../../blank-slate";
import { FileIcon } from "../../file-upload";
import { useTranslation } from "../../localization";
import { cn } from "../../utils";
import type { GetFileAnnotations, SaveFileAnnotations } from "../shared";
import { LightboxAnnotatedImage } from "./lightbox-annotated-image";
import type { LightboxAnnotationControls, LightboxItem } from "./shared";

/** Props Interface */
interface LightboxFocusItemProps {
	currentItem: LightboxItem | undefined;
	getAnnotations?: GetFileAnnotations;
	onSaveAnnotations?: SaveFileAnnotations;
	onControlsChange?: (controls: LightboxAnnotationControls | null) => void;
	footerSlot?: HTMLElement | null;
}

export function LightboxFocusItem({
	currentItem,
	getAnnotations,
	onSaveAnnotations,
	onControlsChange,
	footerSlot,
}: LightboxFocusItemProps) {
	/** Hooks */
	const { t } = useTranslation();

	/** Computed */
	const isImage = currentItem?.contentType.startsWith("image/");
	const isVideo = currentItem?.contentType.startsWith("video/");

	/** Functions */
	function handleOnOpenItem() {
		if (!currentItem) return;
		window.open(currentItem.src);
	}

	/** Render */
	return (
		<motion.div className={cn("w-full h-full flex-center")}>
			{isImage && currentItem ? (
				<LightboxAnnotatedImage
					key={currentItem.itemId}
					fileId={currentItem.itemId}
					src={currentItem.src}
					fileName={currentItem.fileName}
					getAnnotations={getAnnotations}
					onSaveAnnotations={onSaveAnnotations}
					onControlsChange={onControlsChange}
					footerSlot={footerSlot}
				/>
			) : isVideo ? (
				<video
					src={currentItem?.src}
					className="w-8/12 cursor-default max-h-full"
					onClick={(e) => e.stopPropagation()}
					controls
				/>
			) : (
				<object
					data={`${currentItem?.src}#view=FitWidth`}
					className="w-8/12 h-full cursor-default"
					type={currentItem?.contentType}
					onClick={(e) => e.stopPropagation()}
				>
					<div className="flex flex-col justify-center h-full">
						<div className="card h-[300px] rounded-2xl px-2">
							<BlankSlate
								Icon={() => <FileIcon fileType={currentItem?.contentType} />}
								label={`${t("files.open_file")}: ${currentItem?.fileName}`}
								className="cursor-pointer"
								onClick={handleOnOpenItem}
								withAccessory={false}
							/>
						</div>
					</div>
				</object>
			)}
		</motion.div>
	);
}
